import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const ordersService = {
  // Récupérer toutes les commandes
  getOrders: async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      throw error;
    }
  },

  // Récupérer une commande par son ID
  getOrderById: async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la commande ${orderId}:`, error);
      throw error;
    }
  },

  // Créer une nouvelle commande
  createOrder: async (orderData) => {
    try {
      const response = await axios.post(`${API_URL}/orders`, orderData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      throw error;
    }
  },

  // Mettre à jour une commande
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await axios.put(`${API_URL}/orders/${orderId}`, orderData);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la commande ${orderId}:`, error);
      throw error;
    }
  },

  // Mettre à jour le statut d'une commande
  updateOrderStatus: async (orderId, status) => {
    try {
      const response = await axios.patch(`${API_URL}/orders/${orderId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut de la commande ${orderId}:`, error);
      throw error;
    }
  },

  // Mettre à jour le statut de paiement d'une commande
  updatePaymentStatus: async (orderId, paymentStatus) => {
    try {
      const response = await axios.patch(`${API_URL}/orders/${orderId}/payment`, { status: paymentStatus });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut de paiement de la commande ${orderId}:`, error);
      throw error;
    }
  },

  // Supprimer une commande
  deleteOrder: async (orderId) => {
    try {
      const response = await axios.delete(`${API_URL}/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la commande ${orderId}:`, error);
      throw error;
    }
  },

  // Récupérer les statistiques des commandes
  getOrdersStats: async (params = {}) => {
    try {
      const response = await axios.get(`${API_URL}/orders/stats`, { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques de commandes:', error);
      throw error;
    }
  },

  // Récupérer les livraisons
  getDeliveries: async (params = {}) => {
    try {
      const response = await axios.get(`${API_URL}/deliveries`, { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des livraisons:', error);
      throw error;
    }
  },

  // Créer une livraison
  createDelivery: async (deliveryData) => {
    try {
      const response = await axios.post(`${API_URL}/deliveries`, deliveryData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la livraison:', error);
      throw error;
    }
  },

  // Mettre à jour une livraison
  updateDelivery: async (deliveryId, deliveryData) => {
    try {
      const response = await axios.put(`${API_URL}/deliveries/${deliveryId}`, deliveryData);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la livraison ${deliveryId}:`, error);
      throw error;
    }
  },

  // Récupérer les livreurs disponibles
  getDeliveryPersons: async () => {
    try {
      const response = await axios.get(`${API_URL}/delivery-persons`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des livreurs:', error);
      throw error;
    }
  },

  // Récupérer les commandes récentes
  getRecentOrders: async (limit = 5) => {
    try {
      const response = await axios.get(`${API_URL}/orders/recent`, { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes récentes:', error);
      throw error;
    }
  },

  // Rechercher des clients
  searchCustomers: async (query) => {
    try {
      const response = await axios.get(`${API_URL}/customers/search`, { params: { query } });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
      throw error;
    }
  },

  // Récupérer les produits
  getProducts: async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  },

  // --- MÉTHODES D'IMPORTATION SHOPIFY ---

  // Tester la connexion à l'API Shopify
  testShopifyConnection: async (credentials) => {
    try {
      const response = await axios.post(`${API_URL}/integrations/shopify/test-connection`, credentials);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du test de connexion à Shopify:', error);
      throw error;
    }
  },

  // Prévisualiser les commandes Shopify à importer
  previewShopifyOrders: async (credentials, params = {}) => {
    try {
      const response = await axios.post(`${API_URL}/integrations/shopify/preview-orders`, {
        credentials,
        params
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la prévisualisation des commandes Shopify:', error);
      throw error;
    }
  },

  // Importer les commandes depuis Shopify
  importShopifyOrders: async (credentials, params = {}) => {
    try {
      const response = await axios.post(`${API_URL}/integrations/shopify/import-orders`, {
        credentials,
        params
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'importation des commandes Shopify:', error);
      throw error;
    }
  },

  // Importer les commandes depuis un fichier CSV
  importOrdersFromCSV: async (file, options = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('options', JSON.stringify(options));

      const response = await axios.post(`${API_URL}/integrations/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'importation des commandes via CSV:', error);
      throw error;
    }
  },

  // Vérifier les commandes pour détecter les doublons
  checkOrderDuplicates: async (orders) => {
    try {
      const response = await axios.post(`${API_URL}/orders/check-duplicates`, { orders });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification des doublons de commandes:', error);
      throw error;
    }
  }
};

export default ordersService; 